"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScriptBuilder = void 0;
const bn_js_1 = __importDefault(require("bn.js"));
const u_1 = require("../u");
const ContractParam_1 = require("./ContractParam");
const OpCode_1 = require("./OpCode");
const InteropServiceCode_1 = require("./InteropServiceCode");
const CallFlags_1 = require("./CallFlags");
/**
 * Builds a VM script in hexstring. Used for constructing smart contract method calls.
 */
class ScriptBuilder extends u_1.StringStream {
    /**
     * Returns a copy of the script.
     */
    build() {
        return this.str.slice(0);
    }
    /**
     * Appends an Opcode, followed by args
     */
    emit(op, args) {
        this.str += (0, u_1.int2hex)(op);
        if (args) {
            this.str += args;
        }
        return this;
    }
    /**
     * Appends a script that calls an operation on a smart contract.
     * @param scriptHash - ScriptHash of the contract to call.
     * @param operation - The operation to call as a UTF8 string.
     * @param args - Any arguments to pass to the operation.
     * @param callFlags - Flags used while calling the operation.
     *
     * @deprecated Please use emitContractCall which is better typed.
     */
    emitAppCall(scriptHash, operation, args = [], callFlags = CallFlags_1.CallFlags.All) {
        if (args.length === 0) {
            this.emit(OpCode_1.OpCode.NEWARRAY0);
        }
        else {
            for (let i = args.length - 1; i >= 0; i--) {
                this.emitPush(args[i]);
            }
            this.emitNumber(args.length);
            this.emit(OpCode_1.OpCode.PACK);
        }
        return this.emitPush(callFlags)
            .emitString(operation)
            .emitHexString(u_1.HexString.fromHex(scriptHash))
            .emitSysCall(InteropServiceCode_1.InteropServiceCode.SYSTEM_CONTRACT_CALL);
    }
    emitSysCall(service, ...args) {
        for (let i = args.length - 1; i >= 0; i--) {
            this.emitPush(args[i]);
        }
        return this.emit(OpCode_1.OpCode.SYSCALL, service);
    }
    /**
     * Appends data depending on type. Used to append params/array lengths.
     */
    emitPush(data) {
        switch (typeof data) {
            case "boolean":
                return this.emitBoolean(data);
            case "string":
                return this.emitString(data);
            case "number":
                return this.emitNumber(data);
            case "undefined":
                return this.emitPush(false);
            case "object":
                if (Array.isArray(data)) {
                    return this.emitArray(data);
                }
                else if (data instanceof u_1.HexString) {
                    return this.emitHexString(data);
                }
                else if (data === null) {
                    return this.emitPush(false);
                }
                else if ((0, ContractParam_1.likeContractParam)(data)) {
                    return this.emitContractParam(ContractParam_1.ContractParam.fromJson(data));
                }
                throw new Error(`Unidentified object: ${data}`);
            default:
                throw new Error();
        }
    }
    //TODO: Add EmitJump
    //TODO: Add EmitCall(offset)
    emitBoolean(bool) {
        return this.emit(bool ? OpCode_1.OpCode.PUSHT : OpCode_1.OpCode.PUSHF);
    }
    /**
     * Private method to append an array
     */
    emitArray(arr) {
        for (let i = arr.length - 1; i >= 0; i--) {
            this.emitPush(arr[i]);
        }
        return this.emitNumber(arr.length).emit(OpCode_1.OpCode.PACK);
    }
    /**
     * Private method to append a map
     */
    emitMap(m) {
        for (let i = 0; i < m.length; i++) {
            const keyType = m[i].key.type;
            if (!(0, ContractParam_1.isAllowedMapKeyType)(keyType)) {
                throw new Error(`Unsupported key type: ${keyType}`);
            }
            this.emitPush(m[i].value);
            this.emitPush(m[i].key);
        }
        this.emitPush(m.length);
        return this.emit(OpCode_1.OpCode.PACKMAP);
    }
    /**
     * Appends a bytearray.
     */
    emitBytes(byteArray) {
        return this.emitHexString(u_1.HexString.fromArrayBuffer(byteArray, true));
    }
    /**
     * Appends a UTF-8 string.
     */
    emitString(str) {
        const encoder = new TextEncoder();
        const bytes = encoder.encode(str);
        return this.emitBytes(bytes);
    }
    /**
     * Appends a hexstring.
     *
     * @remarks
     * If a Javascript string is provided, it is emitted as it is.
     * If a HexString is provided, it is emitted as little-endian.
     */
    emitHexString(hexstr) {
        if (typeof hexstr === "string") {
            hexstr = u_1.HexString.fromHex(hexstr, true);
        }
        const littleEndianHex = hexstr.toLittleEndian();
        const size = hexstr.byteLength;
        if (size < 0x100) {
            return this.emit(OpCode_1.OpCode.PUSHDATA1, (0, u_1.num2hexstring)(size) + littleEndianHex);
        }
        else if (size < 0x10000) {
            return this.emit(OpCode_1.OpCode.PUSHDATA2, (0, u_1.num2hexstring)(size, 2, true) + littleEndianHex);
        }
        else if (size < 0x100000000) {
            return this.emit(OpCode_1.OpCode.PUSHDATA4, (0, u_1.num2hexstring)(size, 4, true) + littleEndianHex);
        }
        else {
            throw new Error(`Data too big to emit!`);
        }
    }
    /**
     * Helper method to emit a public key.
     *
     * @remarks
     * Conventionally, hexstrings are pushed as little endian into the script. However, for public keys, they are pushed as big endian.
     * This helper method reduces the confusion by hiding this edge case.
     * @param publicKey - 66 character string or a HexString.
     *
     * @example
     * const publicKey = "02028a99826edc0c97d18e22b6932373d908d323aa7f92656a77ec26e8861699ef";
     * const result = new ScriptBuilder()
     *  .emitPublicKey(publicKey)
     *  .build();
     *
     * const publicKeyInHexString = HexString.fromHex(publicKey, false);
     * const sameResult = new ScriptBuilder()
     *  .emitPublicKey(publicKeyInHexString)
     *  .build();
     *
     * console.log(result); // 0c2102028a99826edc0c97d18e22b6932373d908d323aa7f92656a77ec26e8861699ef
     *
     *
     */
    emitPublicKey(publicKey) {
        const stringFormatKey = publicKey instanceof u_1.HexString ? publicKey.toBigEndian() : publicKey;
        return this.emit(OpCode_1.OpCode.PUSHDATA1, (0, u_1.num2hexstring)(33) + stringFormatKey);
    }
    /**
     * Appends a number. Maximum number possible is ulong(256 bits of data).
     * @param num - for numbers beyond MAX_INT, please pass in a string instead of a javascript number.
     */
    emitNumber(num) {
        const bn = new bn_js_1.default(num, 10, "be");
        if (bn.gten(-1) && bn.lten(16)) {
            return this.emit(OpCode_1.OpCode.PUSH0 + bn.toNumber());
        }
        const negative = bn.isNeg();
        const bigint = u_1.BigInteger.fromNumber(num);
        const occupiedBytes = bigint.toReverseTwos().length / 2;
        const targetBytes = this.roundToBestIntSize(occupiedBytes);
        // Catch case 64
        if (targetBytes > 32) {
            throw new Error(`Number too long to be emitted: ${num.toString()}`);
        }
        let hex = bigint.toReverseTwos();
        hex = this.padRight(hex, targetBytes, negative);
        switch (targetBytes) {
            case 1:
                return this.emit(OpCode_1.OpCode.PUSHINT8, hex);
            case 2:
                return this.emit(OpCode_1.OpCode.PUSHINT16, hex);
            case 4:
                return this.emit(OpCode_1.OpCode.PUSHINT32, hex);
            case 8:
                return this.emit(OpCode_1.OpCode.PUSHINT64, hex);
            case 16:
                return this.emit(OpCode_1.OpCode.PUSHINT128, hex);
            case 32:
                return this.emit(OpCode_1.OpCode.PUSHINT256, hex);
            default:
                // Will not reach
                throw new Error();
        }
    }
    roundToBestIntSize(n) {
        switch (true) {
            case n == 1:
                return 1;
            case n == 2:
                return 2;
            case n <= 4:
                return 4;
            case n <= 8:
                return 8;
            case n <= 16:
                return 16;
            case n <= 32:
                return 32;
            default:
                return 64;
        }
    }
    padRight(data, targetLength, isSigned) {
        if (data.length / 2 >= targetLength)
            return data;
        const pad = isSigned ? "ff" : "00";
        while (data.length / 2 != targetLength) {
            data = data + pad;
        }
        return data;
    }
    /**
     * Private method to append a ContractParam
     */
    emitContractParam(param) {
        if (param.type === undefined) {
            throw new Error("No type available!");
        }
        switch (param.type) {
            case ContractParam_1.ContractParamType.Any:
                if (param.value === null) {
                    return this.emit(OpCode_1.OpCode.PUSHNULL);
                }
                return this.emitHexString(param.value ?? "");
            case ContractParam_1.ContractParamType.String:
                return this.emitString(param.value);
            case ContractParam_1.ContractParamType.Boolean:
                return this.emitBoolean(param.value);
            case ContractParam_1.ContractParamType.Integer:
                return this.emitNumber(param.value);
            case ContractParam_1.ContractParamType.ByteArray:
                return this.emitHexString(param.value);
            case ContractParam_1.ContractParamType.Array:
                return this.emitArray(param.value);
            case ContractParam_1.ContractParamType.Hash160:
            case ContractParam_1.ContractParamType.Hash256:
                return this.emitHexString(param.value);
            case ContractParam_1.ContractParamType.PublicKey:
                return this.emitPublicKey(param.value);
            case ContractParam_1.ContractParamType.Map:
                return this.emitMap(param.value);
            default:
                throw new Error(`Unaccounted ContractParamType!: ${param.type}`);
        }
    }
    /**
     * Appends a script that calls an operation on a smart contract.
     * @param contractCall - A ContractCall object emitted from a Contract instance.
     */
    emitContractCall(contractCall) {
        return this.emitAppCall(contractCall.scriptHash, contractCall.operation, contractCall.args, contractCall.callFlags);
    }
    appendScript(script) {
        this.str += script;
        return this;
    }
}
exports.ScriptBuilder = ScriptBuilder;
exports.default = ScriptBuilder;
